import React, { useState, useEffect } from 'react';
import { Receipt, Download, ExternalLink, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../common/Button';
import { useToast } from '../common/Toast';
import { useResponsive } from '../../contexts/ResponsiveContext';
import paymentService, { Invoice, PaymentHistoryParams } from '../../services/paymentService';

interface DateFilter {
  value: string;
  label: string;
  fromDate?: string;
  toDate?: string;
}

interface PurchaseHistoryProps {
  className?: string;
  dateFilters: DateFilter[];
}

export const PurchaseHistory: React.FC<PurchaseHistoryProps> = ({ className = '', dateFilters }) => {
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>('this-month');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();
  const { isMobile: isMobileView } = useResponsive();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Pagination calculations
  const totalPages = Math.ceil(invoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInvoices = invoices.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Smart pagination function to generate page numbers with ellipsis
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3; // Show fewer pages on mobile by default
    
    if (totalPages <= maxVisiblePages + 2) {
      // If total pages is small, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      let startPage, endPage;
      
      if (currentPage <= 3) {
        // If current page is near the beginning
        startPage = 2;
        endPage = Math.min(maxVisiblePages, totalPages - 1);
      } else if (currentPage >= totalPages - 2) {
        // If current page is near the end
        startPage = Math.max(totalPages - maxVisiblePages + 1, 2);
        endPage = totalPages - 1;
      } else {
        // If current page is in the middle
        const around = Math.floor((maxVisiblePages - 1) / 2);
        startPage = Math.max(currentPage - around, 2);
        endPage = Math.min(currentPage + around, totalPages - 1);
      }
      
      // Add ellipsis before start if needed
      if (startPage > 2) {
        pages.push('ellipsis-start');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis after end if needed
      if (endPage < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Load ayment history based on selected filter
  useEffect(() => {
    const fetchPaymentHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        // Reset to first page when filter changes
        setCurrentPage(1);
        
        // Convert date filter to actual date ranges
        const params: PaymentHistoryParams = {};
        
        if (selectedDateFilter !== 'all-time') {
          // Find the selected date filter from the provided filters
          const selectedFilter = dateFilters.find(filter => filter.value === selectedDateFilter);
          
          if (selectedFilter && selectedFilter.fromDate && selectedFilter.toDate) {
            // Use the pre-calculated date range from the filter
            params.from = selectedFilter.fromDate;
            params.to = selectedFilter.toDate;
            console.log(`PurchaseHistory: Using date range from filter: ${selectedFilter.label}`, params);
          } else {
            // Fall back to original calculations if filter doesn't have date range
            const now = new Date();
            
            // Set the "to" date to today
            params.to = now.toISOString();
            
            // Calculate "from" date based on filter
            if (selectedDateFilter === 'current-year') {
              // January 1st of current year
              params.from = new Date(now.getFullYear(), 0, 1).toISOString();
            } else if (selectedDateFilter === 'last-year') {
              // January 1st of last year
              params.from = new Date(now.getFullYear() - 1, 0, 1).toISOString();
              // December 31st of last year
              params.to = new Date(now.getFullYear() - 1, 11, 31).toISOString();
            } else if (selectedDateFilter === 'last-6-months') {
              // 6 months ago
              const sixMonthsAgo = new Date(now);
              sixMonthsAgo.setMonth(now.getMonth() - 6);
              params.from = sixMonthsAgo.toISOString();
            } else if (selectedDateFilter === 'last-3-months') {
              // 3 months ago
              const threeMonthsAgo = new Date(now);
              threeMonthsAgo.setMonth(now.getMonth() - 3);
              params.from = threeMonthsAgo.toISOString();
            }
          }
        }
        
        console.log('Fetching purchase history with params:', params);
        const response = await paymentService.getPaymentHistory(params);
        
        // Process the response to ensure it has the correct structure and check for expired payments
        const processedInvoices = paymentService.processInvoices(response);
        setInvoices(processedInvoices);
      } catch (error) {
        console.error('Error fetching payment history:', error);
        setError('Failed to load payment history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentHistory();
  }, [selectedDateFilter, dateFilters]);

  const formatDate = (dateString: string, useTimeOnly: boolean = false) => {
    try {
      // Handle null, undefined, or invalid date strings
      if (!dateString) {
        console.warn('PurchaseHistory: Invalid date string (null/undefined)');
        return 'N/A';
      }
      
      // Check if the date string is valid
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('PurchaseHistory: Invalid date format:', dateString);
        return 'N/A';
      }
      
      if (useTimeOnly) {
        return new Intl.DateTimeFormat('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }).format(date);
      } else {
        return new Intl.DateTimeFormat('en-US', {
          month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(date);
      }
    } catch (error) {
      console.error('PurchaseHistory: Error formatting date:', error);
      return 'N/A';
    }
  };

  const handleDownloadInvoice = async (invoiceId: string, invoiceNumber: string) => {
    try {
      showToast(`Preparing invoice for download...`, 'info');
      
      const success = await paymentService.downloadInvoice(invoiceId, invoiceNumber);
      
      if (success) {
        showToast(`Invoice downloaded successfully`, 'success');
      } else {
        showToast(`Invoice has not been generated yet. Please try again later.`, 'error');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      showToast(`Error downloading invoice`, 'error');
    }
  };

  return (
    <div className={`bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-3">
            <Receipt className="w-5 h-5 text-primary-light dark:text-primary-dark" />
          </div>
          <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
            Payment History
          </h3>
        </div>
        
        <div className="relative">
          <select
            value={selectedDateFilter}
            onChange={(e) => setSelectedDateFilter(e.target.value)}
            className="appearance-none bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark"
          >
            {dateFilters.map(filter => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-primary-light dark:text-primary-dark animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-8 px-4 bg-red-500/10 rounded-lg">
          <p className="text-red-500 dark:text-red-400 mb-4">{error}</p>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => setSelectedDateFilter(selectedDateFilter)}
          >
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-border-light dark:border-border-dark">
                  <th className="text-sm text-left py-3 px-4 text-text-secondary-light dark:text-text-secondary-dark font-medium">Date</th>
                  <th className="text-sm text-left py-3 px-4 text-text-secondary-light dark:text-text-secondary-dark font-medium">Description</th>
                  <th className="text-sm text-left py-3 px-4 text-text-secondary-light dark:text-text-secondary-dark font-medium">Amount</th>
                  <th className="text-sm text-left py-3 px-4 text-text-secondary-light dark:text-text-secondary-dark font-medium">Payment Method</th>
                  <th className="text-sm text-left py-3 px-4 text-text-secondary-light dark:text-text-secondary-dark font-medium">Status</th>
                  <th className="text-sm text-right py-3 px-4 text-text-secondary-light dark:text-text-secondary-dark font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentInvoices.map((invoice) => (
                  <tr key={invoice.id} className="text-base border-b border-border-light/25 dark:border-border-dark/25 hover:bg-surface-light/25 dark:hover:bg-surface-dark/25 transition-colors">
                    <td className="py-4 px-4">
                      <div className='flex flex-col gap-1'>
                        <p className='font-medium text-text-primary-light dark:text-text-primary-dark'>{formatDate(invoice.date)}</p>
                        <p className='text-sm text-text-secondary-light dark:text-text-secondary-dark'>{formatDate(invoice.date, true)}</p>
                      </div>
                      </td>
                    <td className="py-4 px-4">
                      <div className="font-medium text-text-primary-light dark:text-text-primary-dark">
                        {invoice.items && invoice.items.length > 0 && invoice.items[0] ? invoice.items[0].description : 'Subscription Purchase'}
                      </div>
                      <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        Invoice #{invoice.number}
                      </div>
                    </td>
                    <td className="py-4 px-4 font-medium">
                      {invoice.currency === 'INR' ? '₹' : '$'}{invoice.amount.toFixed(2)}
                    </td>
                    <td className="py-4 px-4">{invoice.paymentMethod == 'netbanking' ? 'Net Banking' : invoice.paymentMethod == 'upi' ? 'UPI' : invoice.paymentMethod == 'card' ? 'Card' : invoice.paymentMethod == 'wallet' ? 'Wallet' : invoice.paymentMethod.charAt(0).toUpperCase() + invoice.paymentMethod.slice(1)}</td>
                    <td className="py-4 px-4">
                      {/** Check if status is pending and date has exceeded by 10 minutes */}
                    {invoice.status === 'pending' && invoice.createdAt && new Date(invoice.createdAt).getTime() < Date.now() - 10 * 60 * 1000 ? (
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400`}>
                        Cancelled
                      </span>
                    ) : (
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        invoice.status === 'paid' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                          : invoice.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    )}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleDownloadInvoice(invoice.id, invoice.number)}
                          title="Download Invoice"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {(totalPages > 1 || invoices.length > itemsPerPage) && (
            <div className="mt-6 pt-4 border-t border-border-light dark:border-border-dark">
              {isMobileView ? (
                // Mobile Layout
                <div className="space-y-3">
                  {/* Page info - centered on mobile */}
                  <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center">
                    Page {currentPage} of {totalPages}
                  </div>
                  
                  {/* Page numbers row */}
                  <div className="flex items-center justify-center gap-1 overflow-x-auto pb-1">
                    {generatePageNumbers().map((page, index) => {
                      if (typeof page === 'string') {
                        // Handle ellipsis
                        return (
                          <span
                            key={page}
                            className="px-2 py-1 text-text-secondary-light dark:text-text-secondary-dark text-sm flex-shrink-0"
                          >
                            ...
                          </span>
                        );
                      }
                      
                      // Handle regular page numbers
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "primary" : "outline"}
                          size="sm"
                          className={`px-2 py-1 text-sm min-w-[32px] flex-shrink-0 ${currentPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Button>
                      );
                    })}
                  </div>
                  
                  {/* Navigation buttons row */}
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<ChevronLeft size={14} />}
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      className="flex-1"
                    >
                      Prev
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      rightIcon={<ChevronRight size={14} />}
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      className="flex-1"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : (
                // Desktop Layout - Original row design
                <div className="flex items-center justify-between">
                  <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<ChevronLeft size={14} />}
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    >
                      Previous
                    </Button>
                    
                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {generatePageNumbers().map((page, index) => {
                        if (typeof page === 'string') {
                          // Handle ellipsis
                          return (
                            <span
                              key={page}
                              className="px-3 py-2 text-text-secondary-light dark:text-text-secondary-dark text-sm"
                            >
                              ...
                            </span>
                          );
                        }
                        
                        // Handle regular page numbers
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "primary" : "outline"}
                            size="sm"
                            className={`px-3 ${currentPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      rightIcon={<ChevronRight size={14} />}
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {invoices.length === 0 && (
            <div className="text-base text-center py-16 text-text-secondary-light dark:text-text-secondary-dark">
              No payment history available for the selected time period.
            </div>
          )}
        </>
      )}
      
      <div className="mt-6 text-sm text-text-secondary-light dark:text-text-secondary-dark">
        <p>Need help with your billing? <a href="#" onClick={(e) => {
          e.preventDefault();
          // Subject: Need help with billing
          window.open('mailto:support@mocktrail.app?subject=Need help with billing', '_blank');
        }} className="text-primary-light dark:text-primary-dark hover:underline">Contact our support team</a>.</p>
      </div>
    </div>
  );
}; 